import React, { useState } from "react";
import "./OrderOverview.css";
import { Link, useNavigate } from "react-router-dom";
import FetchFunc from "../API";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";

const addressRegex = /^([\d/-]+[A-Za-z]?)[ ]+([A-Za-z0-9 '\u2018\u2019.&/()-]+?),[ ]*([A-Za-z0-9 '\u2018\u2019.&/()-]+)[ ]+(NSW|VIC|QLD|WA|SA|TAS|NT|ACT)[ ]+(\d{4})(?:,[ ]*([A-Za-z0-9 '\u2018\u2019.&/()-]+))?$/i;

const preprocessAddress = (address) => {
    // Ensure a space after commas
    return address.replace(/,([^\s])/g, ', $1').trim();
};

// search box
const parseAddress = (address, shouldLog = true) => {
    const normalizedAddress = preprocessAddress(address);
    const match = normalizedAddress.match(addressRegex);

    if (!match) {
        if (shouldLog) {
            console.error("Invalid address format: " + address);
        }
        return null;
    }

    const [, streetNumber, streetName, suburb, state, postcode] = match;

    return {
        address,
        streetName,
        streetNumber,
        suburb,
        state,
        postcode,
    };
};

const getAddressComponent = (place, type, name = 'long_name') => {
    const component = (place.address_components || []).find((item) => item.types.includes(type));
    return component ? component[name] : '';
};

const parsePlaceAddress = (place) => {
    const formattedAddress = place.formatted_address || '';
    const parsedFormattedAddress = parseAddress(formattedAddress, false);
    if (parsedFormattedAddress) {
        return parsedFormattedAddress;
    }

    const subpremise = getAddressComponent(place, 'subpremise');
    const streetNumber = [subpremise, getAddressComponent(place, 'street_number')].filter(Boolean).join('/');
    const streetName = getAddressComponent(place, 'route', 'short_name') || getAddressComponent(place, 'route');
    const suburb = getAddressComponent(place, 'locality')
        || getAddressComponent(place, 'postal_town')
        || getAddressComponent(place, 'sublocality')
        || getAddressComponent(place, 'administrative_area_level_2');
    const state = getAddressComponent(place, 'administrative_area_level_1', 'short_name');
    const postcode = getAddressComponent(place, 'postal_code');
    const address = formattedAddress || [streetNumber, streetName, suburb, state, postcode].filter(Boolean).join(' ');

    return {
        address,
        streetName,
        streetNumber,
        suburb,
        state,
        postcode,
    };
};

const OrderOverview = () => {
    
    const libraries = ['places'];
    const [formData, setFormData] = useState({
        propertyId: '',
        placeId: '',
        address:'',
        type: '',
        streetNumber: '',
        streetName: '',
        suburb: '',
        state: '',
        roomNumber: 2,
        postcode: ''
    });

    const [query, setQuery] = useState('');
    const [autocomplete, setAutocomplete] = useState(null);
    const navigate = useNavigate();
    // const [bounds, setBounds] = useState(null);

    // useEffect(() => {
    //     if (!window.google || !window.google.maps) {
    //         console.error("Google Maps API is not loaded.");
    //         return;
    //     }
    //     // navigator.geolocation.getCurrentPosition(
    //     //     (position) => {
    //     //         const { latitude, longitude } = position.coords;
    //     //         const userLocation = new window.google.maps.LatLng(latitude, longitude);
    //     //         const radius = 1000000;
    //     //         const circle = new window.google.maps.Circle({
    //     //             center: userLocation,
    //     //             radius: radius,
    //     //         });
    //     //         setBounds(circle.getBounds());
    //     //     },
    //     //     () => {
    //     //         console.error("Fail to get user location")
    //     //     }
    //     // );
    // }, []);

    React.useEffect(() => {
        setFormData((prevData) => ({
            ...prevData,
            address: query,
        }));
    }, [query]);

    const onLoad = (autocompleteInstance) => {
        setAutocomplete(autocompleteInstance);
    };

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: libraries,
    });
    
    if (!isLoaded) {
        return <div>Loading...</div>;
    }

    const onPlaceChanged = () => {
        if (autocomplete !== null) {
            const place = autocomplete.getPlace();
            const parsedAddress = parsePlaceAddress(place);
            const formattedAddress = parsedAddress.address;
            const placeId = place.place_id || '';

            if (!formattedAddress || !placeId) {
                alert('Invalid Address');
                return;
            }

            setQuery(formattedAddress);
            setFormData((prevData) => ({
                ...prevData,
                ...parsedAddress,
                address: formattedAddress,
                placeId,
            }));
            // console.log('Selected Place:', place);
        } else {
            // console.log('Autocomplete is not loaded yet!');
        }
    };

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        setQuery(inputValue);
        setFormData((prevData) => ({
            ...prevData,
            address: inputValue,
            placeId: '',
        }));
    };
    const handleSearch = async (e) => {
        e.preventDefault();
        const parsedAddress = formData.placeId ? null : parseAddress(query);
        if (!formData.placeId && !parsedAddress){
            alert('Invalid Address');
            return;
        }

        const dataToSend = {
            ...formData,
            ...(parsedAddress || {}),
        };
        try {
            // console.log('Data send:', dataToSend);
            const response = await FetchFunc(
                '/search/',
                'POST',
                JSON.stringify(dataToSend)
            );
            if (response.status === 200) {
                // console.log('Response from server:', response);
                const data = await response.json();
                localStorage.setItem('price', data.reportPrice);
                // console.log(data);
                navigate(`/report/${data.propertyId}`, { state: { query }});
            } else if (response.status === 404) {
                navigate(`/bookinspector`, { state: { query }});
            } else if (response.status === 401) {
                localStorage.setItem('isLoggedIn', false);
                localStorage.removeItem('username');
                localStorage.removeItem('role');
                localStorage.removeItem('userId');
                localStorage.removeItem('email');
                localStorage.removeItem('mobile');
                navigate('/login');
            } else {
                navigate(`/bookinspector`, { state: { query }});
                // console.log(response.text());
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
        } catch (error) {
            console.error('Failed to submit address:', error);
        }
    };

    return (
        <div>
            <div className="agent_property_form">                
                <div className="agent_form_section">
                    <h1>Property Details</h1>
                    <hr style={{
                        background: '#DDD', 
                        width: '100%', 
                        marginTop: '5%', 
                        marginBottom: '5%',
                        border: 'none',
                        height: '1px'
                        }} 
                    />
                    <Autocomplete 
                        onLoad={onLoad} 
                        onPlaceChanged={onPlaceChanged}
                        // bounds={bounds}
                        options={{
                            strictBounds: true,
                            types: ['address'],
                            componentRestrictions: { country: "AU" },
                        }}
                    >
                        <input 
                            type="text"
                            value={query}
                            onChange={handleInputChange}
                            placeholder="Property Address*"
                            required
                            style={{
                                padding: '0.625rem',
                                position: 'relative',                     
                                height: '1.875rem',
                                width:'90%',
                                marginRight: '0',
                                border: '1px solid #DDD',
                                outline: 'none',
                                borderRadius: '0.625rem',
                            }}
                        />
                    </Autocomplete>
                    <p>Can't find property?<Link to="/address"><span>Click here</span></Link></p>
                    <h1>Order Overview</h1>
                    <h2>Address: {formData.address}</h2>
                    {/* <h2>Inspector: {id}</h2> */}
                    <h2>ETA: 2-4 Business Days</h2>
                    <button 
                        className="order_now"
                        onClick={handleSearch}
                    >Order Now</button> 
                </div>               
            </div>
        </div>
    );
};

export default OrderOverview;
